import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PhoneInput10 from "@/components/site/PhoneInput10";
import DoctorNameInput, { stripDrPrefix } from "@/components/site/DoctorNameInput";
import { to10Digit, toE164, isValid10Digit } from "@/lib/phone";
import {
    CheckCircle, ArrowLeft, ArrowRight, Briefcase, GraduationCap, Target,
    Lock, Sparkle, IdentificationBadge, PaperPlaneTilt, X,
} from "@phosphor-icons/react";

/* ===========================================================================
   ApplyWizard — Path B Phase 2 (2026-02-25)
   ---------------------------------------------------------------------------
   3-step structured provider application wizard. Replaces the legacy flat
   ApplyForm on /for-doctors. Also drives the magic-link landing page
   /apply-as-provider/:token, where `lockedFields` is set so the lead's
   sales-verified identity (email, phone, clinic, name) cannot be tampered
   with mid-flow.

   Steps:
     1. Practice — clinic + identity + city + scale (cases/month)
     2. Credentials — degree + specialty + council reg + years in practice
     3. Goals — current brand + why Invisique + preferred fee + ToS

   Form state is held flat (single object) so the network payload mirrors
   the input fields 1:1 — easier to debug, no transform layer.
   =========================================================================== */

const STEPS = [
    { id: "practice", label: "Practice", icon: Briefcase },
    { id: "credentials", label: "Credentials", icon: GraduationCap },
    { id: "goals", label: "Goals", icon: Target },
];

const DEGREE_OPTIONS = ["BDS", "MDS", "DDS", "Other"];
const SPECIALTY_OPTIONS = [
    { value: "Orthodontist", label: "Orthodontist" },
    { value: "GP_Dentist", label: "GP / General Dentist" },
    { value: "Pedodontist", label: "Pedodontist (children)" },
    { value: "Prosthodontist", label: "Prosthodontist" },
    { value: "Other", label: "Other" },
];
const CASES_PER_MONTH_OPTIONS = [
    { value: "0-5", label: "0 – 5" },
    { value: "5-15", label: "5 – 15" },
    { value: "15-30", label: "15 – 30" },
    { value: "30+", label: "30+" },
];

const EMPTY_FORM = {
    clinic: "",
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
    clinic_registered_name: "",
    gst_number: "",
    clinic_photo_url: "",
    cases_per_month: "",
    degree: "",
    specialty: "",
    council_registration_number: "",
    state_council: "",
    years_in_practice: "",
    board_certifications: "",
    linkedin_url: "",
    current_aligner_brand: "",
    why_invisique: "",
    primary_patient_demographic: "",
    preferred_consultation_fee: "",
    agreed_to_tos: false,
};


/**
 * @param {object} props
 * @param {object} [props.initial]       Initial form values (used by magic-link prefill)
 * @param {Set<string>} [props.lockedFields] Fields the user cannot edit (sales-verified identity)
 * @param {string} [props.inviteToken]   Magic-link token to send with the payload
 * @param {string} [props.b2bLeadId]     Lead id (mirror of inviteToken for clarity)
 * @param {function} [props.onSuccess]   Called with the application_id on success
 */
export default function ApplyWizard({
    initial = {},
    lockedFields,
    inviteToken,
    b2bLeadId,
    onSuccess,
}) {
    const locked = lockedFields || new Set();
    const [step, setStep] = useState(0);
    // Normalize any prefill phone (from sales magic-link invite) to the
    // 10-digit UI contract so PhoneInput10 renders correctly. Strip any
    // pre-filled "Dr." prefix on the name field — DoctorNameInput shows
    // its own sticky chip so leaving the prefix in the value would
    // double it up.
    const normalisedInitial = { ...initial };
    if (normalisedInitial.phone) {
        normalisedInitial.phone = to10Digit(normalisedInitial.phone);
    }
    if (normalisedInitial.name) {
        normalisedInitial.name = stripDrPrefix(normalisedInitial.name);
    }
    const [form, setForm] = useState({ ...EMPTY_FORM, ...normalisedInitial });
    // iter98.99fj — DCI cert self-upload state. Doctor stages the file
    // before submit; the staging id flows into the application payload so
    // the backend can attach the persisted file onto the b2b_lead.
    const [dciCert, setDciCert] = useState(null);   // { staging_id, filename, size_bytes } | null
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    /* ------------------- step-level validation ------------------- */
    const stepValid = () => {
        if (step === 0) {
            return form.clinic.trim() && form.name.trim() && form.email.trim() && isValid10Digit(form.phone);
        }
        if (step === 1) {
            return form.degree && form.specialty && form.council_registration_number.trim() && form.years_in_practice !== "";
        }
        // step 2 (goals) — ToS is the only hard requirement
        return form.agreed_to_tos;
    };

    const goNext = () => {
        if (!stepValid()) {
            toast.error("Please complete the required fields before continuing");
            return;
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const goPrev = () => setStep((s) => Math.max(0, s - 1));

    /* ------------------- submit ------------------- */
    const submit = async (e) => {
        e?.preventDefault();
        if (!form.agreed_to_tos) {
            toast.error("Please accept the partner terms to continue");
            return;
        }
        setSending(true);
        try {
            const payload = {
                ...form,
                // UI carries 10-digit phone; backend expects E.164.
                phone: toE164(form.phone),
                // Coerce numeric fields — the backend treats "" as a string which
                // Pydantic would reject for Optional[int]; explicit null/undefined
                // is the right shape.
                years_in_practice: form.years_in_practice === "" ? null : Number(form.years_in_practice),
                preferred_consultation_fee: form.preferred_consultation_fee === "" ? null : Number(form.preferred_consultation_fee),
                // Mirror the legacy `message` field from why_invisique so the
                // status-check endpoint + admin queue continue to show a body
                message: form.why_invisique || "",
            };
            if (inviteToken) payload.invite_token = inviteToken;
            if (b2bLeadId) payload.b2b_lead_id = b2bLeadId;
            // iter98.99fj — Attach staged DCI certificate (if doctor uploaded one).
            if (dciCert?.staging_id) payload.dci_staging_id = dciCert.staging_id;

            const r = await api.post("/provider-apply", payload);
            setDone(true);
            toast.success("Application received");
            onSuccess?.(r.data.application_id);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit application");
        }
        setSending(false);
    };

    /* ------------------- success view ------------------- */
    if (done) {
        return (
            <div className="text-center py-12" data-testid="apply-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 grid place-items-center mb-5">
                    <CheckCircle size={32} weight="duotone" className="text-primary" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">Application received.</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                    Our partnership team will review your credentials and get back within 3 business days.
                    You&apos;ll receive an email at <span className="font-semibold text-foreground">{form.email}</span>.
                </p>
            </div>
        );
    }

    /* ------------------- main view ------------------- */
    return (
        <form onSubmit={(e) => e.preventDefault()} className="space-y-7" data-testid="apply-wizard">
            <StepRail current={step} />

            {step === 0 && <PracticeStep form={form} upd={upd} locked={locked} />}
            {step === 1 && <CredentialsStep form={form} upd={upd} dciCert={dciCert} setDciCert={setDciCert} />}
            {step === 2 && <GoalsStep form={form} upd={upd} />}

            {/* Navigation rail — back / next / submit */}
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-border/60">
                {step > 0 ? (
                    <Button
                        type="button" variant="outline"
                        onClick={goPrev}
                        className="rounded-full h-11"
                        data-testid="wizard-back-btn"
                    >
                        <ArrowLeft size={14} className="mr-1.5" /> Back
                    </Button>
                ) : <span />}

                <div className="text-xs text-muted-foreground tabular-nums" data-testid="wizard-step-counter">
                    Step {step + 1} of {STEPS.length}
                </div>

                {step < STEPS.length - 1 ? (
                    <Button
                        type="button"
                        onClick={goNext}
                        className="rounded-full h-11 px-6"
                        data-testid="wizard-next-btn"
                    >
                        Next <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                ) : (
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={sending || !form.agreed_to_tos}
                        className="rounded-full h-11 px-6"
                        data-testid="wizard-submit-btn"
                    >
                        {sending ? "Submitting…" : "Submit application"}
                    </Button>
                )}
            </div>
        </form>
    );
}


/* ===========================================================================
   Step rail — 3 circular nodes + connectors, mirrors the "rhythm of circles"
   UI pattern used across the marketing site.
   =========================================================================== */
function StepRail({ current }) {
    return (
        <div className="flex items-center gap-2 sm:gap-3" data-testid="wizard-rail">
            {STEPS.map((s, i) => {
                const isActive = i === current;
                const isDone = i < current;
                const StepIcon = s.icon;
                return (
                    <React.Fragment key={s.id}>
                        <div className="flex items-center gap-2.5 flex-1 sm:flex-none">
                            <div
                                className={`shrink-0 w-9 h-9 rounded-full grid place-items-center text-xs font-bold ring-4 transition-all
                                    ${isActive ? "bg-primary text-white ring-secondary/40" :
                                      isDone ? "bg-primary/15 text-primary ring-transparent" :
                                      "bg-secondary text-muted-foreground ring-transparent"}`}
                                data-testid={`wizard-node-${s.id}`}
                            >
                                {isDone ? <CheckCircle size={16} weight="fill" /> : <StepIcon size={15} weight={isActive ? "fill" : "duotone"} />}
                            </div>
                            <div className={`text-xs font-semibold uppercase tracking-[0.12em] hidden sm:block whitespace-nowrap
                                ${isActive ? "text-primary" : isDone ? "text-foreground/70" : "text-muted-foreground"}`}>
                                {s.label}
                            </div>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-px ${isDone ? "bg-primary/40" : "bg-border"} transition-colors`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}


/* ===========================================================================
   Step 1 — Practice
   =========================================================================== */
function PracticeStep({ form, upd, locked }) {
    return (
        <div className="space-y-6">
            <StepHeader
                eyebrow="Step 1 — Practice"
                title="Tell us about your clinic."
                helper="A few details so our partnership team can route your application correctly."
            />
            <div className="grid sm:grid-cols-2 gap-4">
                <WField label="Clinic name *" testid="wizard-clinic"
                    value={form.clinic} onChange={(v) => upd("clinic", v)} locked={locked.has("clinic")} />
                <WDoctorNameField label="Your name *" testid="wizard-name"
                    value={form.name} onChange={(v) => upd("name", v)} locked={locked.has("name")} />
                <WField label="Email *" type="email" testid="wizard-email"
                    value={form.email} onChange={(v) => upd("email", v)} locked={locked.has("email")} />
                <WPhoneField label="Phone *" testid="wizard-phone"
                    value={form.phone} onChange={(v) => upd("phone", v)} locked={locked.has("phone")} />
                <WField label="City" testid="wizard-city"
                    value={form.city} onChange={(v) => upd("city", v)} locked={locked.has("city")} />
                <WSelect label="Cases per month" testid="wizard-cases"
                    value={form.cases_per_month} onChange={(v) => upd("cases_per_month", v)}
                    options={CASES_PER_MONTH_OPTIONS} />
                <WField label="Clinic registered name (optional)" testid="wizard-clinic-registered"
                    value={form.clinic_registered_name} onChange={(v) => upd("clinic_registered_name", v)} />
                <WField label="GSTIN (optional)" testid="wizard-gst"
                    value={form.gst_number} onChange={(v) => upd("gst_number", v)} />
            </div>
            <WTextarea label="Clinic address (optional)" rows={2}
                value={form.address} onChange={(v) => upd("address", v)} testid="wizard-address" />
            <WField label="Clinic photo URL (optional — Google Drive / website link)"
                value={form.clinic_photo_url} onChange={(v) => upd("clinic_photo_url", v)}
                testid="wizard-clinic-photo-url"
                helper="Paste any URL where we can preview your clinic. You can upload high-res photos later via your provider portal." />
        </div>
    );
}


/* ===========================================================================
   Step 2 — Credentials
   =========================================================================== */
function CredentialsStep({ form, upd, dciCert, setDciCert }) {
    return (
        <div className="space-y-6">
            <StepHeader
                eyebrow="Step 2 — Credentials"
                title="Your clinical credentials."
                helper="We do a paper-trail review on all partner clinicians, so this is the most important step."
            />
            <div className="grid sm:grid-cols-2 gap-4">
                <WSelect label="Degree *" testid="wizard-degree"
                    value={form.degree} onChange={(v) => upd("degree", v)}
                    options={DEGREE_OPTIONS.map((d) => ({ value: d, label: d }))} />
                <WSelect label="Specialty *" testid="wizard-specialty"
                    value={form.specialty} onChange={(v) => upd("specialty", v)}
                    options={SPECIALTY_OPTIONS} />
                <WField label="Council registration number *" testid="wizard-council-reg"
                    value={form.council_registration_number} onChange={(v) => upd("council_registration_number", v)}
                    helper="e.g. KDC/12345" />
                <WField label="State council *" testid="wizard-state-council"
                    value={form.state_council} onChange={(v) => upd("state_council", v)}
                    helper="e.g. Karnataka Dental Council" />
                <WField label="Years in practice *" type="number" testid="wizard-years"
                    value={form.years_in_practice} onChange={(v) => upd("years_in_practice", v)} />
                <WField label="LinkedIn URL (optional)" testid="wizard-linkedin"
                    value={form.linkedin_url} onChange={(v) => upd("linkedin_url", v)} />
            </div>
            <WTextarea label="Board certifications & additional training (optional)" rows={3}
                value={form.board_certifications} onChange={(v) => upd("board_certifications", v)}
                testid="wizard-certs"
                helper="List any specialty board certs, aligner certifications, post-grad training, fellowships, etc." />

            {/* iter98.99fj — DCI Registration Certificate self-upload. */}
            <DciCertUpload value={dciCert} onChange={setDciCert} />
        </div>
    );
}


/* DCI Registration Certificate self-upload card. Stages the file via
   the public /provider-apply/stage-dci-certificate endpoint and stores
   the returned staging_id in component state. Submit handler ships the
   id to /provider-apply which copies the file onto the b2b_lead. */
function DciCertUpload({ value, onChange }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pick = () => {
        if (uploading) return;
        fileRef.current?.click();
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (e.target) e.target.value = "";
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("File too large (max 8 MB)");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const r = await api.post("/provider-apply/stage-dci-certificate", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange({
                staging_id: r.data.staging_id,
                filename: r.data.filename,
                size_bytes: r.data.size_bytes,
            });
            toast.success("Certificate uploaded — we'll attach it to your application");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const remove = () => {
        if (uploading) return;
        onChange(null);
    };

    return (
        <div className="rounded-2xl border border-border bg-secondary/20 p-5" data-testid="wizard-dci-upload">
            <div className="flex items-center gap-2 mb-1">
                <IdentificationBadge size={16} weight="duotone" className="text-primary" />
                <Label className="font-semibold text-sm">
                    DCI Registration Certificate (recommended)
                </Label>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Upload a clean scan of your Dental Council of India registration certificate. We use this to fast-track your verification — applicants who upload here typically clear review the same day.
            </p>

            <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={handleFile}
                data-testid="wizard-dci-file-input"
            />

            {value ? (
                <div className="flex items-start gap-3 flex-wrap rounded-xl border border-emerald-200 bg-emerald-50/60 p-3"
                    data-testid="wizard-dci-uploaded-row">
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-emerald-100 grid place-items-center text-emerald-700">
                        <CheckCircle size={16} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" data-testid="wizard-dci-filename">
                            {value.filename}
                        </div>
                        <div className="text-[11px] text-emerald-700/80">
                            Uploaded · we&apos;ll attach this when you submit the application.
                        </div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <Button type="button" size="sm" variant="outline"
                            onClick={pick} disabled={uploading}
                            data-testid="wizard-dci-replace-btn">
                            Replace
                        </Button>
                        <Button type="button" size="sm" variant="ghost"
                            onClick={remove} disabled={uploading}
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid="wizard-dci-remove-btn">
                            <X size={12} weight="bold" />
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-3 flex-wrap rounded-xl border border-dashed border-border bg-background p-4">
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-secondary grid place-items-center text-muted-foreground">
                        <PaperPlaneTilt size={16} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">Choose certificate file</div>
                        <div className="text-[11px] text-muted-foreground">PDF / JPG / PNG / WebP / HEIC · max 8 MB</div>
                    </div>
                    <Button type="button" size="sm"
                        onClick={pick} disabled={uploading}
                        data-testid="wizard-dci-upload-btn">
                        {uploading ? "Uploading…" : "Upload"}
                    </Button>
                </div>
            )}
        </div>
    );
}


/* ===========================================================================
   Step 3 — Goals
   =========================================================================== */
function GoalsStep({ form, upd }) {
    return (
        <div className="space-y-6">
            <StepHeader
                eyebrow="Step 3 — Goals"
                title="What you're hoping to do with Invisique™."
                helper="Helps us tier you correctly and pair you with the right COE planner."
            />
            <div className="grid sm:grid-cols-2 gap-4">
                <WField label="Current aligner brand (if any)" testid="wizard-current-brand"
                    value={form.current_aligner_brand} onChange={(v) => upd("current_aligner_brand", v)}
                    helper="Invisalign, ClearCorrect, in-house, none, etc." />
                <WField label="Your preferred consultation fee (₹)" type="number" testid="wizard-fee"
                    value={form.preferred_consultation_fee} onChange={(v) => upd("preferred_consultation_fee", v)}
                    helper="This is what patients pay you for an initial consult — keep your usual rate." />
            </div>
            <WTextarea label="Primary patient demographic (optional)" rows={2}
                value={form.primary_patient_demographic} onChange={(v) => upd("primary_patient_demographic", v)}
                testid="wizard-demographic"
                helper="e.g. Young professionals 25-40, post-treatment retainer patients, etc." />
            <WTextarea label="Why Invisique™?" rows={3}
                value={form.why_invisique} onChange={(v) => upd("why_invisique", v)}
                testid="wizard-why-invisique"
                helper="Tell us a bit about what drew you to apply — case mix, training interests, etc." />

            {/* ToS click-through — gate the submit button */}
            <label className="flex items-start gap-2.5 text-xs leading-snug text-muted-foreground cursor-pointer select-none p-4 rounded-xl bg-secondary/40 border border-border/60" data-testid="wizard-tos-label">
                <input
                    type="checkbox"
                    checked={form.agreed_to_tos}
                    onChange={(e) => upd("agreed_to_tos", e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-border accent-primary cursor-pointer"
                    data-testid="wizard-tos-checkbox"
                />
                <span>
                    I confirm the credentials and practice details above are accurate to the best of my knowledge,
                    and I agree to Invisique™&apos;s <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">partner terms &amp; conditions</a>.
                    Submitting an application does not guarantee approval — every applicant goes through a credential review.
                </span>
            </label>
        </div>
    );
}


/* ===========================================================================
   Shared primitives
   =========================================================================== */
function StepHeader({ eyebrow, title, helper }) {
    return (
        <div>
            <div className="overline text-primary mb-2 flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> {eyebrow}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-1.5">{title}</h3>
            <p className="text-sm text-muted-foreground">{helper}</p>
        </div>
    );
}

function WField({ label, value, onChange, type = "text", testid, locked, helper }) {
    return (
        <div>
            <Label className="flex items-center gap-1.5">
                {label}
                {locked && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                        <Lock size={10} weight="fill" /> locked
                    </span>
                )}
            </Label>
            <Input
                type={type}
                value={value}
                onChange={(e) => !locked && onChange(e.target.value)}
                disabled={locked}
                className={`h-11 mt-1.5 ${locked ? "bg-secondary/40 text-muted-foreground" : ""}`}
                data-testid={testid}
            />
            {helper && <p className="text-xs text-muted-foreground mt-1.5">{helper}</p>}
        </div>
    );
}

/* Doctor-name variant of WField — renders the sticky "Dr." chip and
 * auto-strips any leading "dr"/"Dr"/"Dr." the user types. Used by the
 * Practice step for the applicant's name. */
function WDoctorNameField({ label, value, onChange, testid, locked }) {
    return (
        <div>
            <Label className="flex items-center gap-1.5">
                {label}
                {locked && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                        <Lock size={10} weight="fill" /> locked
                    </span>
                )}
            </Label>
            <DoctorNameInput
                value={value}
                onChange={onChange}
                locked={locked}
                placeholder=""
                inputClassName="h-11"
                className="h-11 mt-1.5"
                testid={testid}
            />
        </div>
    );
}

/* Phone variant of WField — renders the +91 country-code chip and
 * clamps the input to 10 numeric digits. Used by the Practice step
 * for the applicant's mobile. */
function WPhoneField({ label, value, onChange, testid, locked }) {
    return (
        <div>
            <Label className="flex items-center gap-1.5">
                {label}
                {locked && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                        <Lock size={10} weight="fill" /> locked
                    </span>
                )}
            </Label>
            <PhoneInput10
                value={value}
                onChange={onChange}
                locked={locked}
                inputClassName="h-11"
                className="h-11 mt-1.5"
                testid={testid}
            />
        </div>
    );
}

function WTextarea({ label, value, onChange, rows = 3, testid, helper }) {
    return (
        <div>
            <Label>{label}</Label>
            <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                className="mt-1.5"
                data-testid={testid}
            />
            {helper && <p className="text-xs text-muted-foreground mt-1.5">{helper}</p>}
        </div>
    );
}

function WSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label>{label}</Label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-11 mt-1.5 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid={testid}
            >
                <option value="">Select…</option>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}
